import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Trash2, ShieldAlert, XCircle } from "lucide-react";

interface DeleteAllDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

const CONFIRMATION_TEXT = "EFFACER TOUTES MES DONNÉES";
const COUNTDOWN_SECONDS = 10;

export function DeleteAllDataDialog({
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteAllDataDialogProps) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [acknowledgments, setAcknowledgments] = useState({
    understand: false,
    noRecovery: false,
    loseClients: false,
    loseReservations: false,
  });
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [countdownActive, setCountdownActive] = useState(false);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setConfirmText("");
      setAcknowledgments({
        understand: false,
        noRecovery: false,
        loseClients: false,
        loseReservations: false,
      });
      setCountdown(COUNTDOWN_SECONDS);
      setCountdownActive(false);
    }
  }, [open]);

  // Countdown timer for final step
  useEffect(() => {
    if (step === 3 && !countdownActive) {
      setCountdownActive(true);
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [step, countdownActive]);

  useEffect(() => {
    if (!countdownActive || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdownActive, countdown]);

  const allAcknowledged = Object.values(acknowledgments).every(Boolean);
  const textMatches = confirmText === CONFIRMATION_TEXT;
  const canProceedToStep2 = allAcknowledged;
  const canProceedToStep3 = textMatches;
  const canDelete = countdown === 0 && !isDeleting;

  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-3 text-destructive text-xl">
            <div className="p-2 bg-destructive/10 rounded-full">
              <ShieldAlert className="w-6 h-6" />
            </div>
            SUPPRESSION DÉFINITIVE DES DONNÉES
          </AlertDialogTitle>
        </AlertDialogHeader>

        {/* Warning Banner */}
        <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-4 my-2">
          <div className="flex gap-3">
            <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="font-bold text-destructive">
                ⚠️ ATTENTION : ACTION IRRÉVERSIBLE
              </p>
              <p className="text-sm text-foreground">
                Vous êtes sur le point de <strong>supprimer définitivement</strong> toutes vos données.
                Cette action ne peut <strong>EN AUCUN CAS</strong> être annulée.
              </p>
            </div>
          </div>
        </div>

        {/* Step 1: Acknowledgments */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <XCircle className="w-4 h-4 text-destructive" />
                Ce que vous allez perdre :
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li><strong>Toutes les réservations</strong> - passées, présentes et futures</li>
                <li><strong>Tous les clients</strong> - noms, emails, téléphones, historique</li>
                <li><strong>Tous les logs d'emails</strong> - confirmations, rappels envoyés</li>
                <li><strong>Tous les tokens de réservation</strong></li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                Étape 1/3 : Confirmez que vous comprenez les conséquences
              </p>
              
              <div className="space-y-3 bg-card border rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acknowledgments.understand}
                    onCheckedChange={(checked) =>
                      setAcknowledgments((prev) => ({ ...prev, understand: !!checked }))
                    }
                  />
                  <span className="text-sm">
                    Je comprends que cette action est <strong className="text-destructive">permanente et irréversible</strong>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acknowledgments.noRecovery}
                    onCheckedChange={(checked) =>
                      setAcknowledgments((prev) => ({ ...prev, noRecovery: !!checked }))
                    }
                  />
                  <span className="text-sm">
                    Je comprends qu'il n'existe <strong className="text-destructive">aucun moyen de récupérer</strong> ces données après suppression
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acknowledgments.loseClients}
                    onCheckedChange={(checked) =>
                      setAcknowledgments((prev) => ({ ...prev, loseClients: !!checked }))
                    }
                  />
                  <span className="text-sm">
                    Je comprends que je vais perdre <strong className="text-destructive">tous mes clients</strong> et leur historique
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acknowledgments.loseReservations}
                    onCheckedChange={(checked) =>
                      setAcknowledgments((prev) => ({ ...prev, loseReservations: !!checked }))
                    }
                  />
                  <span className="text-sm">
                    Je comprends que je vais perdre <strong className="text-destructive">toutes mes réservations</strong>
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Type confirmation text */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                Étape 2/3 : Tapez le texte de confirmation
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Pour confirmer que vous souhaitez vraiment supprimer toutes vos données, 
                  tapez exactement le texte suivant :
                </p>
                <div className="bg-card border-2 border-dashed border-destructive rounded p-3 text-center">
                  <code className="text-destructive font-bold text-lg tracking-wide">
                    {CONFIRMATION_TEXT}
                  </code>
                </div>
              </div>

              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Tapez le texte ci-dessus..."
                className={`text-center font-mono ${
                  confirmText && !textMatches 
                    ? "border-destructive focus-visible:ring-destructive" 
                    : textMatches 
                      ? "border-green-500 focus-visible:ring-green-500" 
                      : ""
                }`}
              />
              
              {confirmText && !textMatches && (
                <p className="text-xs text-destructive text-center">
                  Le texte ne correspond pas. Vérifiez les majuscules et les espaces.
                </p>
              )}
              {textMatches && (
                <p className="text-xs text-green-600 text-center font-medium">
                  ✓ Texte confirmé
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Final countdown */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                Étape 3/3 : Confirmation finale
              </p>
              
              <div className="bg-destructive/5 border border-destructive rounded-lg p-6 text-center space-y-4">
                <Trash2 className="w-12 h-12 text-destructive mx-auto" />
                <div>
                  <p className="text-lg font-bold text-destructive">
                    Dernière chance d'annuler
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Une fois que vous cliquerez sur "Supprimer définitivement", 
                    toutes vos données seront perdues à jamais.
                  </p>
                </div>
                
                {countdown > 0 && (
                  <div className="bg-card border rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      Le bouton sera actif dans
                    </p>
                    <p className="text-3xl font-bold text-destructive mt-1">
                      {countdown} seconde{countdown > 1 ? "s" : ""}
                    </p>
                  </div>
                )}
                
                {countdown === 0 && (
                  <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
                    <p className="text-destructive font-bold">
                      ⚠️ Le bouton de suppression est maintenant actif
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep((prev) => prev - 1)}
              className="sm:mr-auto"
              disabled={isDeleting}
            >
              ← Retour
            </Button>
          )}
          
          <AlertDialogCancel disabled={isDeleting}>
            Annuler
          </AlertDialogCancel>
          
          {step === 1 && (
            <Button
              onClick={() => setStep(2)}
              disabled={!canProceedToStep2}
              variant="destructive"
            >
              Continuer →
            </Button>
          )}
          
          {step === 2 && (
            <Button
              onClick={() => setStep(3)}
              disabled={!canProceedToStep3}
              variant="destructive"
            >
              Continuer →
            </Button>
          )}
          
          {step === 3 && (
            <Button
              onClick={handleConfirm}
              disabled={!canDelete}
              variant="destructive"
              className="gap-2"
            >
              {isDeleting ? (
                "Suppression en cours..."
              ) : countdown > 0 ? (
                `Attendre ${countdown}s...`
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Supprimer définitivement
                </>
              )}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
